import React, { useState } from 'react';
import InputField from '../../../../Components/Form/components/InputField';
import InputWrapper from '../../../../Components/Form/components/InputWrapper/InputWrapper';
import PasswordField from '../../../Base/Components/Inputs/PasswordField/PasswordField';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
// @ts-ignore
import styles from 'SUKLCredentialsSetupForm.module.scss';
import apiClient from '../../../../Services/ApiClient';
import { useAppContext } from '../../../../Services/AppContext';
import useApiClient from '../../../Base/Hooks/useApiClient';
import SUKLCredentialsHintModal from '../SUKLCredentialsHint/SUKLCredentialsHintModal';
import useNotification from '../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../Hooks/useTranslation';
import { Meta, useLazyMyMetaQuery } from '../../../../graphql/graphql';
import { useEffectAsync } from '../../../../helpers';
import Loading from '../../../../Components/Loading';
import { saveToUserMeta } from '../../../User/Services/userMeta.service';
import { MetaKey } from '../../../Meta/Enums/MetaKey.enum';
import { findInMeta } from '../../../Meta/Services/meta.service';

interface Props {
	onSuccess: () => void;
}

const validationSchema = yup.object({
	uuid: yup.string().notRequired(),
	password: yup.string().notRequired(),
});

const SUKLCredentialsSetupForm: React.FC<Props> = ({ onSuccess }) => {
	const { userData } = useAppContext();
	const { displayError } = useApiClient();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [isHintOpen, setIsHintOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [getMyMeta] = useLazyMyMetaQuery();
	const [passwordInMeta, setPasswordInMeta] = useState(false);

	useEffectAsync(async () => {
		setIsLoading(true);
		const defaultData: { uuid?: string; password?: string } = {};
		const data = await getMyMeta().unwrap();
		const SUKLLogin = findInMeta({
			meta: data?.me?.meta as Meta[],
			metaKey: MetaKey.SUKL_LOGIN,
		});
		const SUKLPassword = findInMeta({
			meta: data?.me?.meta as Meta[],
			metaKey: MetaKey.SUKL_PASSWORD,
		});
		if (SUKLLogin?.value) {
			defaultData.uuid = SUKLLogin.value;
		}
		if (SUKLPassword?.value) {
			defaultData.password = '******';
			setPasswordInMeta(true);
		}
		formMethods.reset(defaultData);
		setIsLoading(false);
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	const handleSaveCredentials = async (
		suklLogin?: string,
		suklPassword?: string,
	) => {
		await saveToUserMeta({
			userId: userData.id,
			key: MetaKey.SUKL_LOGIN,
			value: suklLogin ?? '',
		});
		await saveToUserMeta({
			userId: userData.id,
			key: MetaKey.SUKL_PASSWORD,
			value: suklPassword ?? '',
		});
		showNotificationSuccess({ content: t('component.missingInfo.text5') });
		onSuccess();
	};

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		const suklLogin = values.uuid;
		const suklPassword = values.password
			? values.password === '******'
				? undefined
				: values.password
			: undefined;
		try {
			if (suklLogin && (suklPassword || passwordInMeta)) {
				//IF HAVE BOTH OR HAVE LOGIN AND META PASS - DOING VALIDITY TEST
				const result = await apiClient.post('/sukl/test-cert', {
					suklLogin,
					suklPassword,
				});
				if (result.status === 200) {
					await handleSaveCredentials(suklLogin, suklPassword);
				}
			} else {
				//DONT HAVE BOTH - DIRECTLY SAVING VALUES
				await handleSaveCredentials(suklLogin, suklPassword);
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<FormWrapper formMethods={formMethods}>
				<InputField
					name='uuid'
					label='component.certificates.enterYourLogin'
					className='mb-3'
					isRequired
				/>
				<InputWrapper>
					<PasswordField
						name='password'
						label='component.searchMedicamentRecordAuthorization.password'
						isRequired
					/>
				</InputWrapper>
				<div className={styles.controls}>
					<SubmitButton onSubmit={handleSubmit} title='base.shared.save' />
					<LinkButton
						title='component.certificates.whereToGetSUKLLogin'
						theme={Theme.BLUE}
						onClick={() => setIsHintOpen(true)}
					/>
				</div>
			</FormWrapper>
			{isHintOpen && (
				<SUKLCredentialsHintModal
					onClose={() => setIsHintOpen(false)}
					onConfirm={() => setIsHintOpen(false)}
				/>
			)}
		</div>
	);
};

export default SUKLCredentialsSetupForm;
